


import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import {
  Menu,
  ChevronLeft,
  Disc,
  Package,
  Settings,
  Megaphone,
  Store as StoreIcon,
  BarChart3,
  LogOut,
  Ticket,
  Target,
  Info,
  Palette,
  LayoutDashboard,
  ChevronRight,
  FileSpreadsheet,
  Ban
} from 'lucide-react';

import api from '../api/api';

const Sidebar = ({ hide }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isCampaignActive, setIsCampaignActive] = useState(true);

  const navigate = useNavigate();

  // USER DATA
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  })();

  const designation = user?.designation?.trim() || '';

  // ROLES
  const isAdmin =
    designation === 'Admin' ||
    designation === 'Super Admin';

  const isStore =
    designation === 'Store';

  const isUser =
    designation === 'User';

  // CHECK CAMPAIGN STATUS FOR STORE
  useEffect(() => {
    if (isStore && user?.id) {
      api
        .get(`/api/campaign/verify?storeId=${user.id}`)
        .then((response) => {
          const data = response.data;

          if (data?.success) {
            setIsCampaignActive(data.isActive);
          }
        })
        .catch((err) => {
          console.error(
            'Sidebar campaign check failed:',
            err
          );
        });
    }
  }, [isStore, user?.id]);

  if (hide) {
    return null;
  }

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/login');
  };

  const renderNavItem = ({
    to,
    label,
    Icon,
    iconColor,
    end = false
  }) => {
    return (
      <NavLink
        to={to}
        end={end}
        aria-label={label}
        title={!isOpen ? label : undefined}
        className={({ isActive }) => `
          group
          relative
          flex
          items-center
          ${isOpen ? 'gap-2.5 px-3 py-1.5' : 'justify-center p-1.5'}
          w-full
          rounded-xl
          transition-all
          duration-200
          ${
            isActive
              ? 'bg-slate-900 text-white shadow-md shadow-slate-200'
              : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
          }
        `}
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-[#F6C453]" />
            )}

            <span
              className={`
                flex
                items-center
                justify-center
                shrink-0
                w-7
                h-7
                rounded-lg
                transition-all
                duration-200
                ${
                  isActive
                    ? 'bg-white/10'
                    : 'bg-slate-50 group-hover:bg-white'
                }
              `}
            >
              <Icon
                size={16}
                color={
                  isActive
                    ? '#F6C453'
                    : iconColor
                }
                strokeWidth={2}
              />
            </span>

            {isOpen && (
              <>
                <span
                  className={`
                    flex-1
                    text-xs
                    font-semibold
                    tracking-wide
                    whitespace-nowrap
                    ${
                      isActive
                        ? 'text-white'
                        : 'text-slate-700'
                    }
                  `}
                >
                  {label}
                </span>

                <ChevronRight
                  size={14}
                  className={`
                    transition-all
                    duration-200
                    ${
                      isActive
                        ? 'text-[#F6C453] opacity-100'
                        : 'text-slate-300 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0'
                    }
                  `}
                />
              </>
            )}

            {!isOpen && (
              <span className="pointer-events-none absolute left-[calc(100%+12px)] top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold whitespace-nowrap opacity-0 translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 shadow-xl z-[100]">
                {label}
              </span>
            )}
          </>
        )}
      </NavLink>
    );
  };

  const sectionTitle = (title) => {
    if (!isOpen) {
      return <div className="h-1.5" />;
    }

    return (
      <div className="px-3 pt-1.5 pb-0.5">
        <span className="text-[9px] font-bold tracking-[0.2em] text-slate-400 uppercase">
          {title}
        </span>
      </div>
    );
  };

  return (
    <aside
      className={`
        relative
        h-screen
        shrink-0
        transition-[width]
        duration-300
        ease-in-out
        ${isOpen ? 'w-[260px]' : 'w-[80px]'}
      `}
      style={{ zIndex: 50 }}
    >
      <div className="relative flex flex-col h-screen bg-white border-r border-slate-200 shadow-[8px_0_30px_rgba(15,23,42,0.05)] overflow-hidden">

        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#E6B94A] via-[#FFD970] to-[#D9A934]" />

        {/* BRAND HEADER */}
        <div
          className={`shrink-0 px-4 pt-14 pb-2 ${
            isOpen
              ? 'text-left'
              : 'flex justify-center'
          }`}
        >
          <div
            className={`flex items-center ${
              isOpen
                ? 'gap-2.5'
                : 'justify-center'
            }`}
          >
            <div className="w-9 h-9 shrink-0 rounded-xl bg-slate-900 flex items-center justify-center shadow-md shadow-slate-200 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-white/10" />

              <Disc
                size={18}
                color="#F6C453"
                strokeWidth={2}
              />
            </div>

            {isOpen && (
              <div className="min-w-0">
                <p className="text-[14px] font-extrabold tracking-wide text-slate-900 leading-none">
                  SPIN & WIN
                </p>

                <p className="text-[8px] font-semibold tracking-[0.18em] text-slate-400 uppercase mt-1">
                  Campaign Management
                </p>
              </div>
            )}
          </div>

          {isOpen && (
            <div className="mt-2 h-px bg-slate-100" />
          )}
        </div>

        {/* ROLE BADGE */}
        {isOpen && designation && (
          <div className="px-4 pb-1.5 shrink-0">
            <div className="rounded-xl border border-amber-100 bg-gradient-to-r from-amber-50 to-white px-3 py-1">
              <p className="text-[8px] font-bold tracking-[0.18em] text-amber-500 uppercase">
                Current Role
              </p>

              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F6C453] shadow-[0_0_0_2px_rgba(246,196,83,0.2)]" />

                <p className="text-[11px] font-bold text-slate-800 truncate">
                  {designation}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* NAVIGATION AREA */}
        <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-visible px-3 space-y-0 sidebar-scroll">

          {/* =========================================================
              STORE
              ONLY:
              - Spin Wheel
              - Store QR
             ========================================================= */}

          {isStore && (
            <>
              {sectionTitle('Campaign')}

              {isCampaignActive &&
                renderNavItem({
                  to: '/',
                  label: 'Spin Wheel',
                  Icon: Disc,
                  iconColor: '#D4A72C',
                  end: true,
                })}

              {sectionTitle('Tools')}

              {renderNavItem({
                to: '/code-redemption',
                label: 'Store QR',
                Icon: Ticket,
                iconColor: '#EAB308',
              })}
            </>
          )}

          {/* =========================================================
              USER
              ONLY:
              - Reports
             ========================================================= */}

          {isUser && (
            <>
              {sectionTitle('Analytics')}

              {renderNavItem({
                to: '/reports',
                label: 'Reports',
                Icon: BarChart3,
                iconColor: '#EC4899',
              })}
            </>
          )}

          {/* =========================================================
              ADMIN / SUPER ADMIN
              EXISTING ALL PAGES
             ========================================================= */}

          {isAdmin && (
            <>
              {/* CAMPAIGN */}
              {sectionTitle('Campaign')}

              {renderNavItem({
                to: '/',
                label: 'Spin Wheel',
                Icon: Disc,
                iconColor: '#D4A72C',
                end: true,
              })}

              {/* MANAGEMENT */}
              {sectionTitle('Management')}

              {renderNavItem({
                to: '/products',
                label: 'Products',
                Icon: Package,
                iconColor: '#3B82F6',
              })}

              {renderNavItem({
                to: '/admin',
                label: 'Admin Panel',
                Icon: Settings,
                iconColor: '#8B5CF6',
              })}

              {renderNavItem({
                to: '/announcements',
                label: 'Banners',
                Icon: Megaphone,
                iconColor: '#F97316',
              })}

              {renderNavItem({
                to: '/stores_detail',
                label: 'Stores',
                Icon: StoreIcon,
                iconColor: '#22C55E',
              })}

              {renderNavItem({
                to: '/redeemcode',
                label: 'Redemption Import',
                Icon: FileSpreadsheet,
                iconColor: '#10B981',
              })}

              {renderNavItem({
                to: '/blocked-numbers',
                label: 'Blocked Numbers',
                Icon: Ban,
                iconColor: '#EF4444',
              })}

              {/* ANALYTICS */}
              {sectionTitle('Analytics')}

              {renderNavItem({
                to: '/dashboard',
                label: 'Dashboard',
                Icon: LayoutDashboard,
                iconColor: '#6366F1',
              })}

              {renderNavItem({
                to: '/reports',
                label: 'Reports',
                Icon: BarChart3,
                iconColor: '#EC4899',
              })}

              {renderNavItem({
                to: '/campaign-stats',
                label: 'Campaign Stats',
                Icon: Target,
                iconColor: '#10B981',
              })}

              {/* TOOLS */}
              {sectionTitle('Tools')}

              {renderNavItem({
                to: '/code-redemption',
                label: 'Store QR',
                Icon: Ticket,
                iconColor: '#EAB308',
              })}

              {renderNavItem({
                to: '/theme-manager',
                label: 'Theme Manager',
                Icon: Palette,
                iconColor: '#7C3AED',
              })}
            </>
          )}

        </nav>

        {/* BOTTOM ACTION AREA */}
        <div className="shrink-0 px-3 py-2 bg-white border-t border-slate-100">
          <div
            className={`flex items-center ${
              isOpen
                ? 'gap-2'
                : 'flex-col gap-2'
            }`}
          >
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Logout"
              title={!isOpen ? 'Logout' : undefined}
              className={`
                group
                relative
                flex
                items-center
                ${
                  isOpen
                    ? 'flex-1 gap-2.5 px-3'
                    : 'justify-center w-9'
                }
                h-9
                rounded-xl
                text-red-500
                hover:bg-red-50
                hover:text-red-600
                transition-all
                duration-200
              `}
            >
              <LogOut
                size={16}
                className="shrink-0"
              />

              {isOpen && (
                <span className="text-xs font-semibold">
                  Logout
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() =>
                setIsOpen(!isOpen)
              }
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 transition"
            >
              {isOpen ? (
                <ChevronLeft size={16} />
              ) : (
                <Menu size={16} />
              )}
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;